import { useState } from "react";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/base/StatusBadge";
import {
  ORDER_SOURCE_CONFIG,
  ORDER_STATUS_FILTERS,
  PAYMENT_CONFIG,
  PICKUP_CONFIG,
} from "@/constants/status";
import { activeOrdersList } from "@/mocks/dashboard";
import type { OrderStatus } from "@/types";
import { formatSAR } from "@/utils/format";

export default function ActiveOrders() {
  const [filter, setFilter] = useState<OrderStatus | "all">("all");

  const visibleOrders =
    filter === "all"
      ? activeOrdersList
      : activeOrdersList.filter((order) => order.status === filter);

  return (
    <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between px-5 pt-5">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-flashlight-line"></i>
          </span>
          <h3 className="text-base font-bold text-foreground-950">
            الطلبات النشطة
          </h3>
        </div>
        <Link
          to="/orders"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          عرض الكل
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-s-line"></i>
          </span>
        </Link>
      </div>

      <div className="px-5 pt-4 pb-1 flex flex-wrap gap-2">
        {ORDER_STATUS_FILTERS.map((item) => {
          const active = filter === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                active
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <p className="md:hidden px-5 pt-3 flex items-center gap-1.5 text-xs text-foreground-500">
        <span className="w-4 h-4 flex items-center justify-center text-primary-500">
          <i className="ri-drag-move-2-line"></i>
        </span>
        اسحب الجدول أفقيًا لعرض بقية الأعمدة
      </p>

      <div className="relative mt-3">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 md:hidden bg-gradient-to-r from-white via-white/70 to-transparent"></div>
        <div className="overflow-x-auto pb-1">
        {visibleOrders.length === 0 ? (
          <div className="py-14 text-center">
            <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
              <i className="ri-inbox-archive-line text-2xl"></i>
            </div>
            <p className="text-sm text-foreground-500">
              لا توجد طلبات بهذه الحالة حاليًا
            </p>
          </div>
        ) : (
          <table className="w-full min-w-[920px] text-right border-collapse">
            <thead>
              <tr className="text-xs text-foreground-500 bg-background-50">
                <th className="font-medium px-5 py-3 whitespace-nowrap">
                  رقم الطلب
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  الفرع
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  المصدر
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  وقت الطلب
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  المنتجات
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  المبلغ
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  الحالة
                </th>
                <th className="font-medium px-3 py-3 whitespace-nowrap">
                  الاستلام
                </th>
                <th className="font-medium px-5 py-3 whitespace-nowrap">
                  الدفع
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order) => {
                const payment = PAYMENT_CONFIG[order.payment];
                const pickup = PICKUP_CONFIG[order.pickup];
                const source = ORDER_SOURCE_CONFIG[order.source];
                return (
                  <tr
                    key={order.id}
                    className="border-t border-background-100 hover:bg-primary-50/60 transition-colors duration-200"
                  >
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-bold text-foreground-950">
                        #{order.id}
                      </span>
                      <p className="text-xs text-foreground-400 mt-0.5">
                        {order.customer}
                      </p>
                    </td>
                    <td className="px-3 py-3.5 text-sm text-foreground-700 whitespace-nowrap">
                      {order.branch}
                    </td>
                    <td className="px-3 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${source.className}`}
                        title={source.label}
                      >
                        <i className={source.icon}></i>
                        {source.short}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 text-sm text-foreground-500 whitespace-nowrap">
                      {order.timeAgo}
                    </td>
                    <td className="px-3 py-3.5 text-sm text-foreground-700">
                      {order.items}
                    </td>
                    <td className="px-3 py-3.5 text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      {formatSAR(order.amount)}
                    </td>
                    <td className="px-3 py-3.5">
                      <StatusBadge status={order.status} pickup={order.pickup} />
                    </td>
                    <td className="px-3 py-3.5">
                      <span
                        className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap"
                        title={pickup.label}
                      >
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                          <i className={pickup.icon}></i>
                        </span>
                        {pickup.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap"
                        title={payment.label}
                      >
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                          <i className={payment.icon}></i>
                        </span>
                        {payment.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    </section>
  );
}